import React, { useEffect, useState } from 'react';
import { safeArray, unwrapListResponse } from '../../components/ErrorBoundary';
import ProjectFilterSelect from '../../components/ProjectFilterSelect';
import TablePagination from '../../components/TablePagination';
import ProjectNameText from '../../components/ProjectNameText';
import TabbedSection from '../../components/TabbedSection';
import { useProjects } from '../../context/ProjectsContext';
import { submittalsAPI } from '../../services/authService';
import { formatDateIN } from '../../utils/dateFormat';
import SubmittalStatusBadge, { formatSubmittalStatus } from './SubmittalStatusBadge';

const REPORT_TABS = [
  { key: 'project-register', label: 'Project Register', loader: (p) => submittalsAPI.getReportProjectRegister(p), paginated: true },
  { key: 'category-status', label: 'Category Status', loader: (p) => submittalsAPI.getReportCategoryStatus(p), paginated: false },
  { key: 'pending-client', label: 'Pending Client', loader: (p) => submittalsAPI.getReportPendingWithClient(p), paginated: true },
  { key: 'pending-consultant', label: 'Pending Consultant', loader: (p) => submittalsAPI.getReportPendingWithConsultant(p), paginated: true },
  { key: 'pending-internal', label: 'Pending Internal', loader: (p) => submittalsAPI.getReportPendingWithInternal(p), paginated: true },
  { key: 'overdue', label: 'Overdue', loader: (p) => submittalsAPI.getReportOverdue(p), paginated: true },
  { key: 'sla-delay', label: 'SLA Delay', loader: (p) => submittalsAPI.getReportSlaDelay(p), paginated: true },
  { key: 'cycle-time', label: 'Approval Cycle Time', loader: (p) => submittalsAPI.getReportApprovalCycleTime(p), paginated: false },
  { key: 'revision-history', label: 'Revision History', loader: (p) => submittalsAPI.getReportRevisionHistory(p), paginated: false },
];

const formatPendingParty = (row) => {
  if (row.current_pending_user?.name) return row.current_pending_user.name;
  if (row.pending_party?.name) return row.pending_party.name;
  if (row.responsible_party) return formatSubmittalStatus(row.responsible_party);
  if (row.current_pending_party_type) return formatSubmittalStatus(row.current_pending_party_type);
  return '—';
};

const SubmittalReports = ({ onViewSubmittal }) => {
  const { projects } = useProjects();
  const [activeTab, setActiveTab] = useState('project-register');
  const [projectFilter, setProjectFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const perPage = 50;

  const tabConfig = REPORT_TABS.find((t) => t.key === activeTab) || REPORT_TABS[0];

  useEffect(() => {
    setPage(1);
  }, [activeTab, projectFilter]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = tabConfig.paginated ? { page, pageSize: perPage } : {};
        if (projectFilter !== 'all') params.projectId = projectFilter;
        const resp = await tabConfig.loader(params);
        if (tabConfig.paginated) {
          const { rows: dataRows, totalCount: cnt } = unwrapListResponse(resp);
          if (!cancelled) { setRows(safeArray(dataRows)); setTotalCount(cnt); }
        } else if (!cancelled) {
          setRows(safeArray(resp?.data?.data));
          setTotalCount(safeArray(resp?.data?.data).length);
        }
      } catch (err) {
        console.warn('Report load failed:', err?.message || err);
        if (!cancelled) { setRows([]); setTotalCount(0); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [activeTab, page, projectFilter, tabConfig]);

  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));

  const renderTable = () => {
    if (activeTab === 'category-status') {
      return (
        <table className="data-table">
          <thead><tr><th>Category</th><th>Status</th><th>Count</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={3}>Loading...</td></tr> : rows.length === 0 ? (
              <tr><td colSpan={3} style={{ textAlign: 'center', color: '#6b7280' }}>No data</td></tr>
            ) : rows.map((row, i) => (
              <tr key={`${row.category_id}-${row.status}-${i}`}>
                <td>{row.category?.name || row.category_id}</td>
                <td><SubmittalStatusBadge status={row.status} /></td>
                <td>{row.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (activeTab === 'sla-delay') {
      return (
        <table className="data-table">
          <thead>
            <tr>
              <th>Submittal No.</th>
              <th>Project</th>
              <th>Category</th>
              <th>Subject</th>
              <th>Revision</th>
              <th>Pending Party</th>
              <th>Status</th>
              <th>Due Date</th>
              <th>Delay (days)</th>
              <th>Last Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={10}>Loading...</td></tr> : rows.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', color: '#6b7280' }}>No data</td></tr>
            ) : rows.map((row) => (
              <tr key={row.id} style={{ cursor: 'pointer' }} onClick={() => onViewSubmittal?.(row.id)}>
                <td style={{ fontWeight: 600, color: '#7C3AED' }}>{row.submittal_no || `#${row.id}`}</td>
                <td><ProjectNameText as="span">{row.project?.name || '—'}</ProjectNameText></td>
                <td>{row.category?.name || '—'}</td>
                <td>{row.subject}</td>
                <td>{row.current_revision_label || '—'}</td>
                <td>{formatPendingParty(row)}</td>
                <td><SubmittalStatusBadge status={row.status} isOverdue={row.is_overdue} /></td>
                <td>{formatDateIN(row.due_date)}</td>
                <td>{row.delay_days ?? 0}</td>
                <td>{formatDateIN(row.last_action_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (activeTab === 'cycle-time') {
      return (
        <table className="data-table">
          <thead><tr><th>Submittal</th><th>Project</th><th>Submitted</th><th>Approved</th><th>Cycle (days)</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5}>Loading...</td></tr> : rows.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: '#6b7280' }}>No data</td></tr>
            ) : rows.map((row) => (
              <tr key={row.id} style={{ cursor: 'pointer' }} onClick={() => onViewSubmittal?.(row.id)}>
                <td>{row.submittal_no || row.subject}</td>
                <td><ProjectNameText as="span">{row.project?.name || '—'}</ProjectNameText></td>
                <td>{formatDateIN(row.submitted_at)}</td>
                <td>{formatDateIN(row.approved_at)}</td>
                <td>{row.cycle_days ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (activeTab === 'revision-history') {
      return (
        <table className="data-table">
          <thead><tr><th>Submittal</th><th>Revision</th><th>Status</th><th>Submitted At</th><th>Remarks</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5}>Loading...</td></tr> : rows.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: '#6b7280' }}>No data</td></tr>
            ) : rows.map((row, i) => (
              <tr key={`${row.submittal_id}-${row.revision_no}-${i}`} style={{ cursor: 'pointer' }} onClick={() => onViewSubmittal?.(row.submittal_id)}>
                <td>{row.submittal_no || row.subject}</td>
                <td>{row.revision_label || `R${row.revision_no}`}</td>
                <td><SubmittalStatusBadge status={row.revision_status || row.status} /></td>
                <td>{formatDateIN(row.submitted_at)}</td>
                <td>{row.remarks || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return (
      <table className="data-table">
        <thead>
          <tr>
            <th>Submittal No.</th>
            <th>Project</th>
            <th>Subject</th>
            <th>Status</th>
            <th>Due Date</th>
            <th>Aging</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={6}>Loading...</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={6} style={{ textAlign: 'center', color: '#6b7280' }}>No data</td></tr>
          ) : rows.map((row) => (
            <tr key={row.id} style={{ cursor: 'pointer' }} onClick={() => onViewSubmittal?.(row.id)}>
              <td style={{ fontWeight: 600, color: '#7C3AED' }}>{row.submittal_no || `#${row.id}`}</td>
              <td><ProjectNameText as="span">{row.project?.name || '—'}</ProjectNameText></td>
              <td>{row.subject}</td>
              <td><SubmittalStatusBadge status={row.status} isOverdue={row.is_overdue} /></td>
              <td>{formatDateIN(row.due_date)}</td>
              <td>{row.aging_days ?? 0}d</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Submittal Reports</h2>
          <p style={{ color: '#6b7280', margin: 0 }}>Analytics and registers for submittal workflow tracking.</p>
        </div>
      </div>

      <TabbedSection
        activeKey={activeTab}
        onChange={setActiveTab}
        tabs={REPORT_TABS.map((t) => ({ key: t.key, label: t.label, content: null }))}
      />

      <div className="filters-row" style={{ marginBottom: 12 }}>
        <ProjectFilterSelect value={projectFilter} onChange={setProjectFilter} projects={projects} />
      </div>

      <div className="table-container">{renderTable()}</div>
      {tabConfig.paginated && (
        <TablePagination page={page} totalPages={totalPages} totalCount={totalCount} onPageChange={setPage} />
      )}
    </div>
  );
};

export default SubmittalReports;
